import {
	ActionBarPrimitive,
	AssistantIf,
	BranchPickerPrimitive,
	ComposerPrimitive,
	ErrorPrimitive,
	MessagePrimitive,
	TextMessagePartProvider,
	ThreadPrimitive,
	useAssistantApi,
	useAssistantEvent,
	useAssistantState,
	useMessagePartText,
	useThreadViewport,
} from '@assistant-ui/react'
import {
	ArrowDownIcon,
	ArrowUpIcon,
	CheckIcon,
	ChevronLeftIcon,
	ChevronRightIcon,
	CopyIcon,
	DownloadIcon,
	SquareIcon,
} from 'lucide-react'
import {
	createContext,
	type FC,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'
import { AIDisclaimer } from '#app/components/assistant-ui/ai-disclaimer'
import {
	//ComposerAddAttachment,
	ComposerAttachments,
	UserMessageAttachments,
} from '#app/components/assistant-ui/attachment'
import { MarkdownText } from '#app/components/assistant-ui/markdown-text'
import { ToolFallback } from '#app/components/assistant-ui/tool-fallback'
import { TooltipIconButton } from '#app/components/assistant-ui/tooltip-icon-button'
import { SnabbSok } from '#app/components/snabb-sok'
import { Button } from '#app/components/ui/button'
import {
	VoiceInputButton,
	voiceInputSupported,
} from '#app/components/voice-input-button'
import { visibleAnswerText } from '#app/utils/answer-restart.js'
import { type ChatVariant } from '#app/utils/chat-query.js'
import { renumberCitations } from '#app/utils/citations.js'
import { cn } from '#app/utils/misc.tsx'
import {
	chatLocation,
	durationBucket,
	queryLenBucket,
	trackCopy,
	trackUIEvent,
	trackVoiceInput,
	wordCount,
} from '#app/utils/plausible.client.js'
import { prepareRutaText } from '#app/utils/rutor.ts'
import { rememberVoiceText } from '#app/utils/voice-input-origin.ts'
import { InThreadAd } from './ads'
import { MessageRating } from './message-rating'
import {
	CitationNumberingProvider,
	MessageSources,
	useCitationNumbering,
} from './message-sources'
import {
	RegionChip,
	RegionHint,
	RegionUnderRow,
	useRegion,
} from './region-picker'
import { RutorFooter, rutorForCopy } from './rutor'
import { SearchLoadingIndicator } from './search-loading'

/**
 * Exposes the chat variant ('full' | 'embed') to descendants so leaf
 * components (citation-bubble, message-sources) can map it to a Plausible
 * `location` ('assistent_start_page' | 'assistent_embed') without re-threading
 * the prop through every level of the tree.
 */
export const ThreadVariantContext = createContext<ChatVariant>('full')

/**
 * The Snabbsök query + the page's region, handed from Thread (which captures
 * the first question at run-start) down to UserMessage, where the card renders
 * under the first question bubble. Null = nothing to render (embed, flag off,
 * or no question yet).
 */
const SnabbsokContext = createContext<{ query: string; region: string } | null>(
	null,
)

export const Thread: FC<{ variant?: ChatVariant }> = ({ variant = 'full' }) => {
	const api = useAssistantApi()
	// Snabbsök runtime kill-switch: window.ENV.ENABLE_SNABBSOK === '1'. When
	// off (default), the card never renders and we don't even capture the
	// user query - prod is byte-identical to today.
	// Never in embed: the embed surface is intentionally stripped-down, so
	// gating here disables both query-capture and rendering in one place.
	const snabbsokEnabled =
		typeof window !== 'undefined' &&
		window.ENV?.ENABLE_SNABBSOK === '1' &&
		variant !== 'embed'
	// First user query in the thread, captured at run-start. Snabbsök triggers
	// ONCE per thread (the first question) — NOT on follow-ups.
	//
	// Why first-question-only: the card renders under the first question (see
	// UserMessageSnabbsok). On a follow-up the run-start auto-scroll (below) pins
	// the latest user message to the top, pushing that spot above the viewport —
	// so a re-fetched card is never actually seen, yet it would still fire an
	// impression. Triggering only on the first question keeps every impression a
	// genuinely-seen one and stops invisible follow-up impressions from diluting
	// CTR. A new conversation remounts this whole subtree (ChatPage is keyed on
	// conversationId, see chat-page.tsx), so each new thread gets a fresh
	// first-question trigger automatically — no manual reset needed.
	const [snabbsokQuery, setSnabbsokQuery] = useState('')
	// The page's ONLY region choice (?region=, see region-picker.tsx). The
	// Snabbsök card follows it instead of having its own picker.
	const { region } = useRegion()

	// Reserve the sidebar ad rail as soon as the conversation starts (non-embed
	// + thread no longer empty) rather than when the ad finishes loading — so the
	// chat shifts once, together with the field appearing, instead of jumping
	// again when a late ad pops in. On the empty start page the chat stays full-
	// width and centered. The chat column stays viewport-centered while there's
	// slack, then slides left so it never collides with the right-pinned ad
	// field. Driven by a breakpoint CSS var `--adw` (the ad field width:
	// 0 / 280 / 320px) and a self-zeroing right padding on the viewport — below.
	// Kept in sync with SideBarAd in ads.tsx (both gate on a non-empty thread).
	const threadIsEmpty = useAssistantState(({ thread }) => thread.isEmpty)
	const showRail = variant !== 'embed' && !threadIsEmpty

	useAssistantEvent('thread.run-start', () => {
		if (!snabbsokEnabled) return
		// Read state imperatively (not via useAssistantState) so we don't
		// subscribe to the full message-list update stream, which would
		// re-render on every streamed token.
		const messages = api.thread().getState().messages
		// First question only: at this run-start the thread holds exactly one
		// user message (the one just submitted). A follow-up turn has >= 2, so we
		// leave snabbsokQuery — and thus the card — untouched.
		const userMessages = messages.filter(m => m.role === 'user')
		if (userMessages.length !== 1) return
		const txt = userMessages[0].content
			.map((c: { type: string; text?: string }) =>
				c.type === 'text' ? c.text ?? '' : '',
			)
			.join(' ')
			.trim()
		if (txt) setSnabbsokQuery(txt)
	})

	useAssistantEvent('thread.run-start', event => {
		// Blur the active element to close the keyboard on mobile
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}

		// Scroll the last user message to the top of the viewport
		// Use requestAnimationFrame to wait for DOM updates and layout calculations
		let rafId1: number
		let rafId2: number

		rafId1 = requestAnimationFrame(() => {
			rafId2 = requestAnimationFrame(() => {
				const viewport = document.querySelector('.aui-thread-viewport')
				const roleSelector =
					variant === 'embed' ? '[data-role="assistant"]' : '[data-role="user"]'
				const messages = viewport?.querySelectorAll(roleSelector)
				const targetMessage = messages?.[messages.length - 1]

				if (targetMessage && viewport) {
					const viewportRect = viewport.getBoundingClientRect()
					const messageRect = targetMessage.getBoundingClientRect()
					const scrollOffset = messageRect.top - viewportRect.top

					viewport.scrollBy({ top: scrollOffset, behavior: 'smooth' })
				}
			})
		})

		// Cleanup function to cancel animation frames if component unmounts
		return () => {
			cancelAnimationFrame(rafId1)
			cancelAnimationFrame(rafId2)
		}
	})

	return (
		<ThreadVariantContext.Provider value={variant}>
			<SnabbsokContext.Provider
				value={
					snabbsokEnabled && snabbsokQuery
						? { query: snabbsokQuery, region }
						: null
				}
			>
			<ThreadPrimitive.Root className="aui-root aui-thread-root @container relative flex h-full flex-col bg-background">
				{/* "Ny konversation" button slot.
				    Two placements depending on variant:
				    - Full variant: lives INSIDE the scroll viewport, above the
				      messages, as a left-aligned `sticky` element; once the user
				      scrolls into the chat it pins to the top-corner (sticky
				      top-2) and floats over the messages. (Snabbsök used to sit
				      above it; it now renders under the first question.)
				    - Embed variant: lives OUTSIDE the viewport as an absolute-
				      positioned element in the top-left corner. Embed has no
				      Snabbsök context to coordinate with and the popup chrome is
				      narrower, so absolute corner-pin matches the existing prod
				      behaviour.
				    Only ONE slot renders at a time (variant is exclusive) so the
				    shared `id="new-chat-slot"` portal target stays unambiguous
				    for NewChatButton / OpenInAssistantButton. The `empty:hidden`
				    class keeps it invisible when nothing is portaled in. */}
				{variant === 'embed' && (
					<div
						id="new-chat-slot"
						className="absolute left-2 top-2 z-20 empty:hidden lg:left-4 lg:top-4"
					/>
				)}
				<ThreadPrimitive.Viewport
					turnAnchor="top"
					scrollToBottomOnRunStart={false}
					className={cn(
						'aui-thread-viewport relative flex flex-1 flex-col overflow-x-hidden overflow-y-scroll scroll-smooth pt-4',
						// Adaptive horizontal padding. Without the rail: plain px-4
						// (chat centered as before). With the rail: keep the chat
						// viewport-centered while the right gutter can hold the ad
						// field, then grow right-padding so the centered column slides
						// left to clear it. `--adw` is the ad field width per
						// breakpoint (0 / 280 / 320px); the calc self-zeroes (floored
						// at 1rem) on wide screens and when no ad is present.
						//
						// The calc is `2*--adw + 1088px - 100vw`, where 1088px =
						// chat column 1024 (max-w-5xl) + pl-4 (16) + 2×24px gap to the
						// field. `--adw` is doubled because the chat is centered, so
						// clearing the right field by X means giving up X on both
						// sides. Keep --adw/breakpoints in sync with SideBarAd in
						// ads.tsx.
						showRail
							? 'pl-4 pr-4 [--adw:0px] min-[1344px]:pr-[max(1rem,calc(var(--adw)_+_var(--adw)_+_1088px_-_100vw))] min-[1344px]:[--adw:280px] min-[1700px]:[--adw:320px]'
							: 'px-4',
					)}
				>
					<AssistantIf condition={({ thread }) => thread.isEmpty}>
						<ThreadWelcome />
					</AssistantIf>

					{variant !== 'embed' && (
						// "Ny konversation" slot, taken verbatim from the LIVE fly
						// demo (deploy/fly-team-demo @ df0b665 in 260513). Once
						// the user scrolls into the chat the OUTER wrapper pins to
						// the viewport top (`sticky top-2`). The wrapper is
						// `max-w-5xl mx-auto` so its left edge lines up with the
						// chat message column (not the raw viewport edge — that
						// alignment fix wasn't on the older feature/snabbsok-
						// search-quality branch I first looked at; this is the
						// deployed version on internetmedicin-snabbsok-demo.fly.dev).
						// `pointer-events-none` on the wrapper means the
						// full-width transparent strip never blocks clicks /
						// selection on the messages or Snabbsök links beneath it;
						// only the button itself (`pointer-events-auto`,
						// shrink-wrapped by the flex parent) is interactive.
						// `empty:hidden` collapses the slot when the button is
						// hidden (empty thread). Skipped in embed (resets by
						// reload — embed retains the prod absolute-corner slot
						// above for OpenInAssistantButton).
						<div className="pointer-events-none sticky top-2 z-20 mx-auto flex w-full max-w-5xl lg:top-4">
							<div
								id="new-chat-slot"
								className="pointer-events-auto empty:hidden"
							/>
						</div>
					)}

					<ThreadPrimitive.Messages
						components={{
							UserMessage,
							// EditComposer, -- no editing of messages for now
							AssistantMessage,
						}}
					/>

					<ThreadPrimitive.ViewportFooter className="aui-thread-viewport-footer sticky bottom-0 mx-auto mt-auto flex w-full max-w-5xl flex-col gap-4 overflow-visible rounded-t-3xl bg-background pb-[max(1rem,env(safe-area-inset-bottom))] md:pb-[max(1.5rem,env(safe-area-inset-bottom))]">
						<ThreadScrollToBottom />
						{variant === 'embed' ? (
							<Composer autoFocus />
						) : (
							<AssistantIf condition={({ thread }) => !thread.isEmpty}>
								<Composer placeholder="Ställ en följdfråga" regionChip />
							</AssistantIf>
						)}
					</ThreadPrimitive.ViewportFooter>
				</ThreadPrimitive.Viewport>
			</ThreadPrimitive.Root>
			</SnabbsokContext.Provider>
		</ThreadVariantContext.Provider>
	)
}

const ThreadScrollToBottom: FC = () => {
	const scrollToBottom = useThreadViewport(s => s.scrollToBottom)

	const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault()
		scrollToBottom({ behavior: 'instant' })

		// Blur any active element (like the composer input) to close keyboard
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}

		// Manually trigger scroll to bottom

		// Blur the button itself
		e.currentTarget.blur()
	}

	return (
		<ThreadPrimitive.ScrollToBottom asChild>
			<TooltipIconButton
				tooltip="Scrolla till botten"
				variant="outline"
				className="aui-thread-scroll-to-bottom absolute -top-12 z-10 size-9 self-center rounded-full disabled:invisible dark:bg-background dark:hover:bg-accent"
				onClick={handleClick}
			>
				<ArrowDownIcon />
			</TooltipIconButton>
		</ThreadPrimitive.ScrollToBottom>
	)
}

const ThreadWelcome: FC = () => {
	const variant = useContext(ThreadVariantContext)
	if (variant !== 'embed') {
		return <StartScreen />
	}
	return (
		<div className="aui-thread-welcome-root mx-auto my-auto flex w-full max-w-5xl grow flex-col">
			<div className="aui-thread-welcome-center flex w-full grow flex-col items-center justify-center">
				<div className="aui-thread-welcome-message flex size-full flex-col justify-center px-4">
					<h1 className="aui-thread-welcome-message-inner text-2xl font-semibold duration-200 animate-in fade-in slide-in-from-bottom-1">
						Ställ en fråga
					</h1>
					<p className="aui-thread-welcome-message-inner text-xl text-muted-foreground delay-75 duration-200 animate-in fade-in slide-in-from-bottom-1">
						AI-assistent baserad på Internetmedicins innehåll samt externa,
						betrodda källor
					</p>
				</div>
			</div>
		</div>
	)
}

const TYPEWRITER_EXAMPLES = [
	'När ska man misstänka lungemboli?',
	'Hur skiljer man pneumoni från KOL-exacerbation?',
	'Varför försvinner alla mina pennor?',
	'Hur utreder man nyupptäckt förmaksflimmer?',
	'Hur behandlas migrän i första linjen?',
	'Hur titrerar man kaffe till klinisk effekt?',
	'Hur utreder man makroskopisk hematuri?',
	'Hur skiljer man depression från utmattningssyndrom?',
	'Är silvertejp en godkänd medicinsk sutur?',
	'Hur ställer man en hypotyreos-diagnos?',
	'Hur följer man upp typ 2-diabetes i primärvård?',
] as const

function useTypewriter(words: readonly string[], paused = false) {
	const [text, setText] = useState('')
	const [phase, setPhase] = useState<'typing' | 'deleting'>('typing')
	const [wordIndex, setWordIndex] = useState(0)

	useEffect(() => {
		// Pause halts the rotation and freezes whatever is currently shown.
		// StartScreen uses `paused` to stop the animation the moment the user
		// interacts with the composer (otherwise the cycling placeholder with
		// its fake caret obscures the user's real text caret — clinicians
		// reported not realising they could type).
		if (paused) return
		if (words.length === 0) return
		const word = words[wordIndex] ?? ''
		let timer: ReturnType<typeof setTimeout> | undefined
		if (phase === 'typing') {
			if (text.length < word.length) {
				timer = setTimeout(() => setText(word.slice(0, text.length + 1)), 28)
			} else {
				timer = setTimeout(() => setPhase('deleting'), 3600)
			}
		} else {
			if (text.length > 0) {
				timer = setTimeout(() => setText(text.slice(0, -1)), 25)
			} else {
				setWordIndex(i => (i + 1) % words.length)
				setPhase('typing')
			}
		}
		return () => {
			if (timer) clearTimeout(timer)
		}
	}, [text, phase, wordIndex, words, paused])

	return text
}

function useBlink(intervalMs = 530) {
	const [on, setOn] = useState(true)
	useEffect(() => {
		const id = setInterval(() => setOn(o => !o), intervalMs)
		return () => clearInterval(id)
	}, [intervalMs])
	return on
}

const StartScreen: FC = () => {
	// First-interaction guard: the composer auto-focuses on mount, which means
	// the user sees the typewriter placeholder + its fake caret animating
	// inside an already-focused field. If they click the textarea to start
	// typing, nothing visually changes (their real caret is hidden by
	// `hideCaretWhenEmpty`), so the experience suggests they can't type yet.
	// `hasInteracted` flips on the first pointerdown inside the composer
	// region: it pauses the typewriter, clears the placeholder so the user's
	// own caret is visible immediately, and turns off `hideCaretWhenEmpty`.
	const [hasInteracted, setHasInteracted] = useState(false)
	const text = useTypewriter(TYPEWRITER_EXAMPLES, hasInteracted)
	const cursorOn = useBlink()
	const placeholder = hasInteracted ? '' : `${text}${cursorOn ? '|' : ' '}`
	return (
		<div className="mx-auto my-auto flex w-full max-w-2xl flex-col items-center justify-center gap-8 px-4 py-12 duration-300 animate-in fade-in slide-in-from-bottom-2">
			<div className="text-center">
				<h1 className="text-3xl font-semibold tracking-tight md:text-4xl">
					Vad vill du veta?
				</h1>
				<p className="mt-3 text-base text-muted-foreground md:text-lg">
					Chatta med alla våra PM och många betrodda externa källor
				</p>
			</div>
			{/* pointerDownCapture catches the click before the textarea's own
			    focus handler, so the typewriter halts on the same tick the
			    user clicks - no stale frame of fake-caret rendering. */}
			<div className="w-full">
				<div onPointerDownCapture={() => setHasInteracted(true)}>
					<Composer
						placeholder={placeholder}
						autoFocus
						hideCaretWhenEmpty={!hasInteracted}
					/>
				</div>
				{/* The region choice as a sentence under the field, exactly like
				    im.se's start page: "Söker i <Alla regioner ▾> (i)". Sits
				    OUTSIDE the pointer-capture wrapper so a region change does not
				    stop the typewriter placeholder. */}
				<RegionUnderRow />
			</div>
		</div>
	)
}

const Composer: FC<{
	placeholder?: string
	autoFocus?: boolean
	hideCaretWhenEmpty?: boolean
	/** Results view: the region pill bottom left in the chat box (variant A). */
	regionChip?: boolean
}> = ({
	placeholder = 'Ställ en fråga',
	autoFocus = false,
	hideCaretWhenEmpty = false,
	regionChip = false,
}) => {
	const variant = useContext(ThreadVariantContext)
	const inputHeightClasses =
		variant === 'embed' ? 'max-h-20 min-h-10' : 'max-h-32 min-h-14'
	return (
		<ComposerPrimitive.Root className="aui-composer-root relative flex w-full flex-col">
			<ComposerPrimitive.AttachmentDropzone className="aui-composer-attachment-dropzone flex w-full flex-col rounded-2xl border border-input bg-background px-1 pt-2 outline-none transition-shadow has-[textarea:focus-visible]:border-ring has-[textarea:focus-visible]:ring-2 has-[textarea:focus-visible]:ring-ring/20 data-[dragging=true]:border-dashed data-[dragging=true]:border-ring data-[dragging=true]:bg-accent/50">
				<ComposerAttachments />
				<ComposerPrimitive.Input
					placeholder={placeholder}
					className={cn(
						'aui-composer-input mb-1 w-full resize-none bg-transparent px-4 pb-3 pt-2 text-base outline-none placeholder:text-muted-foreground focus-visible:ring-0',
						inputHeightClasses,
						hideCaretWhenEmpty && 'placeholder-shown:caret-transparent',
					)}
					rows={1}
					autoFocus={autoFocus}
					aria-label="Meddelandefält"
				/>
				<ComposerAction regionChip={regionChip} />
			</ComposerPrimitive.AttachmentDropzone>
		</ComposerPrimitive.Root>
	)
}

const ComposerAction: FC<{ regionChip?: boolean }> = ({
	regionChip = false,
}) => {
	const api = useAssistantApi()
	const variant = useContext(ThreadVariantContext)
	// Voice input: window.ENV.ENABLE_VOICE_INPUT === '1' and a browser with
	// MediaRecorder. Decided after mount so SSR and the first client render
	// match. Never in embed: the site's iframe lacks allow="microphone", so the
	// button would only give permission errors there.
	const [voiceEnabled, setVoiceEnabled] = useState(false)
	useEffect(() => {
		setVoiceEnabled(
			variant !== 'embed' &&
				window.ENV?.ENABLE_VOICE_INPUT === '1' &&
				voiceInputSupported(),
		)
	}, [variant])
	const appendTranscript = (text: string) => {
		const composer = api.composer()
		const current = composer.getState().text.trim()
		const next = current ? `${current} ${text}` : text
		composer.setText(next)
		// For `input_method` on question_asked (chat-page.tsx).
		rememberVoiceText(next)
		document.querySelector<HTMLTextAreaElement>('.aui-composer-input')?.focus()
	}
	return (
		<div className="aui-composer-action-wrapper relative mx-2 mb-2 flex items-center justify-end gap-2">
			{/*<ComposerAddAttachment />*/}

			{/* Variant A: the region pill on the left in the same row as the send
			    button. `mr-auto` pushes the send button to the right; the pill
			    is small enough (icon + short name on mobile) never to collide. */}
			{regionChip && (
				<div className="relative mr-auto flex min-w-0 items-center">
					<RegionChip />
					{/* The one-time hint sits to the RIGHT of the pill (its arrow points at it). */}
					<RegionHint />
				</div>
			)}

			{voiceEnabled && (
				<AssistantIf condition={({ thread }) => !thread.isRunning}>
					<VoiceInputButton
						onText={appendTranscript}
						className={regionChip ? undefined : 'mr-auto'}
						errorAlign={regionChip ? 'right' : 'left'}
						onOutcome={(outcome, mode, { durationMs, text, ...details }) =>
							// The transcript stays here: only word count and bands go on.
							trackVoiceInput({
								outcome,
								mode,
								location: 'assistent_start_page',
								// Attempt, help text and failure signals (IM-128).
								...details,
								durationBucket:
									durationMs === undefined
										? undefined
										: durationBucket(durationMs),
								queryLenBucket: text ? queryLenBucket(text) : undefined,
								wordCount: text ? wordCount(text) : undefined,
							})
						}
					/>
				</AssistantIf>
			)}

			<AssistantIf condition={({ thread }) => !thread.isRunning}>
				<ComposerPrimitive.Send asChild>
					<TooltipIconButton
						tooltip="Skicka meddelande"
						side="bottom"
						type="submit"
						variant="default"
						size="icon"
						className="aui-composer-send size-8 rounded-full"
						aria-label="Skicka meddelande"
					>
						<ArrowUpIcon className="aui-composer-send-icon size-4" />
					</TooltipIconButton>
				</ComposerPrimitive.Send>
			</AssistantIf>

			<AssistantIf condition={({ thread }) => thread.isRunning}>
				<ComposerPrimitive.Cancel asChild>
					<Button
						type="button"
						variant="default"
						size="icon"
						className="aui-composer-cancel size-8 rounded-full"
						aria-label="Avbryt"
					>
						<SquareIcon className="aui-composer-cancel-icon size-3 fill-current" />
					</Button>
				</ComposerPrimitive.Cancel>
			</AssistantIf>
		</div>
	)
}

const MessageError: FC = () => {
	return (
		<MessagePrimitive.Error>
			<ErrorPrimitive.Root className="aui-message-error-root mt-2 rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive dark:bg-destructive/5 dark:text-red-200">
				<ErrorPrimitive.Message className="aui-message-error-message line-clamp-2" />
			</ErrorPrimitive.Root>
		</MessagePrimitive.Error>
	)
}

/**
 * The answer text, minus any partial answer before the "start over" marker
 * (IM-29): when the engine retries after early truncation, the old half answer
 * stays in the text part (the stream can only append), and only what came
 * after the marker should show. Without a marker the part renders as before.
 */
const AnswerText: FC = () => {
	const part = useMessagePartText()
	// Tool boxes: partial markers are hidden and markers on their own line
	// become their own blocks (rutor.ts).
	const visible = prepareRutaText(visibleAnswerText(part.text))
	if (visible === part.text) return <MarkdownText />
	return (
		<TextMessagePartProvider
			text={visible}
			isRunning={part.status.type === 'running'}
		>
			<MarkdownText />
		</TextMessagePartProvider>
	)
}

const AssistantMessage: FC = () => {
	const variant = useContext(ThreadVariantContext)
	return (
		<MessagePrimitive.Root
			className="aui-assistant-message-root relative mx-auto w-full max-w-5xl py-3 duration-150 animate-in fade-in slide-in-from-bottom-1"
			data-role="assistant"
		>
			{/* Bubbles, source list and copy read the same numbering (IM-52). */}
			<CitationNumberingProvider>
				<div className="aui-assistant-message-content overflow-wrap-anywhere break-words px-2 leading-relaxed text-foreground">
					<MessagePrimitive.Parts
						components={{
							Text: AnswerText,
							tools: { Fallback: ToolFallback },
						}}
					/>
					<SearchLoadingIndicator />
					<MessageError />
					<InThreadAd />
				</div>

				<div className="aui-assistant-message-footer ml-2 mt-1 flex flex-col">
					{/* <BranchPicker /> — disabled for assistant messages */}
					{variant !== 'embed' && <AssistantActionBar />}
					<RutorFooter />
					<MessageSources />
					<MessageRating />
					<AIDisclaimer />
				</div>
			</CitationNumberingProvider>
		</MessagePrimitive.Root>
	)
}

/**
 * Copy/export use the VISIBLE answer text (visibleAnswerText), not the
 * primitives' `getCopyText()`, which takes the whole text part - after a retry
 * (IM-29) the old half answer would have come along. Otherwise the same icons
 * and behaviour as ActionBarPrimitive.Copy/ExportMarkdown. Citations are
 * rewritten to the displayed numbers (IM-52), so the copy says the same number
 * as the screen; the history and the rating keep the original numbers.
 */
const AssistantActionBar: FC = () => {
	const api = useAssistantApi()
	const location = chatLocation(useContext(ThreadVariantContext))
	// Copy and download count once per answer and page view (tracking map in
	// plausible.client.ts); the buttons themselves keep working every time.
	const copyTracked = useRef(false)
	const downloadTracked = useRef(false)
	const numbering = useCitationNumbering()
	const visibleText = () => {
		// Tool boxes are copied as a reference with a link, not as a marker.
		const text = rutorForCopy(
			visibleAnswerText(api.message().getCopyText()),
			api.message().getState().metadata as Record<string, unknown> | undefined,
		)
		return numbering ? renumberCitations(text, numbering) : text
	}
	const copy = () => {
		const text = visibleText()
		if (!text) return
		void navigator.clipboard.writeText(text).then(() => {
			if (!copyTracked.current) {
				copyTracked.current = true
				trackCopy({ target: 'svar', location })
			}
			// The same runtime state the primitive sets (message.isCopied).
			api.message().setIsCopied(true)
			setTimeout(() => api.message().setIsCopied(false), 3000)
		})
	}
	const exportMarkdown = () => {
		const text = visibleText()
		if (!text) return
		const url = URL.createObjectURL(new Blob([text], { type: 'text/markdown' }))
		const a = document.createElement('a')
		a.href = url
		a.download = `message-${Date.now()}.md`
		a.click()
		if (!downloadTracked.current) {
			downloadTracked.current = true
			trackUIEvent({
				eventName: 'button_click',
				label: 'download_answer',
				location,
			})
		}
		URL.revokeObjectURL(url)
	}
	return (
		<ActionBarPrimitive.Root
			hideWhenRunning
			autohide="not-last"
			autohideFloat="single-branch"
			className="aui-assistant-action-bar-root data-floating:absolute data-floating:rounded-md data-floating:border data-floating:bg-background data-floating:p-1 data-floating:shadow-sm col-start-3 row-start-2 -ml-1 flex gap-1 text-muted-foreground"
		>
			<TooltipIconButton tooltip="Kopiera" onClick={copy}>
				<AssistantIf condition={({ message }) => message.isCopied}>
					<CheckIcon />
				</AssistantIf>
				<AssistantIf condition={({ message }) => !message.isCopied}>
					<CopyIcon />
				</AssistantIf>
			</TooltipIconButton>
			<TooltipIconButton
				tooltip="Exportera som Markdown"
				onClick={exportMarkdown}
			>
				<DownloadIcon />
			</TooltipIconButton>
		</ActionBarPrimitive.Root>
	)
}

// Snabbsök under the FIRST question only (the query is captured once per
// thread, see run-start above): a text link that expands into the card in
// place. The card follows the page's region choice (never shown in embed).
const UserMessageSnabbsok: FC = () => {
	const snabbsok = useContext(SnabbsokContext)
	const isFirstQuestion = useAssistantState(
		({ thread, message }) =>
			thread.messages.find(m => m.role === 'user')?.id === message.id,
	)
	if (!snabbsok || !isFirstQuestion) return null
	return (
		<div className="col-span-full col-start-1 flex flex-col">
			<SnabbSok query={snabbsok.query} region={snabbsok.region} />
		</div>
	)
}

const UserMessage: FC = () => {
	return (
		<MessagePrimitive.Root
			className="aui-user-message-root mx-auto grid w-full max-w-5xl auto-rows-auto grid-cols-[minmax(72px,1fr)_auto] content-start gap-y-2 px-2 py-3 duration-150 animate-in fade-in slide-in-from-bottom-1 [&:where(>*)]:col-start-2"
			data-role="user"
		>
			<UserMessageAttachments />

			<div className="aui-user-message-content-wrapper relative col-start-2 min-w-0">
				<div className="aui-user-message-content overflow-wrap-anywhere break-words rounded-2xl bg-muted px-4 py-2.5 text-foreground">
					<MessagePrimitive.Parts />
				</div>
			</div>

			<UserMessageSnabbsok />

			<BranchPicker className="aui-user-branch-picker col-span-full col-start-1 row-start-3 -mr-1 justify-end" />
		</MessagePrimitive.Root>
	)
}

const BranchPicker: FC<BranchPickerPrimitive.Root.Props> = ({
	className,
	...rest
}) => {
	return (
		<BranchPickerPrimitive.Root
			hideWhenSingleBranch
			className={cn(
				'aui-branch-picker-root -ml-2 mr-2 inline-flex items-center text-xs text-muted-foreground',
				className,
			)}
			{...rest}
		>
			<BranchPickerPrimitive.Previous asChild>
				<TooltipIconButton tooltip="Föregående">
					<ChevronLeftIcon />
				</TooltipIconButton>
			</BranchPickerPrimitive.Previous>
			<span className="aui-branch-picker-state font-medium">
				<BranchPickerPrimitive.Number /> / <BranchPickerPrimitive.Count />
			</span>
			<BranchPickerPrimitive.Next asChild>
				<TooltipIconButton tooltip="Nästa">
					<ChevronRightIcon />
				</TooltipIconButton>
			</BranchPickerPrimitive.Next>
		</BranchPickerPrimitive.Root>
	)
}
